import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Checkbox, Divider, Typography } from '@mui/material';
import { DirectionsCar, DirectionsWalk } from '@mui/icons-material';
import PropTypes from 'prop-types';

import { Filter } from './Filter';
import { BarChart } from '../Charts';
import { getSales } from '../../../services/ReportService';

import { useStyles } from './CommonStyles.styles';

import { useMenuContext } from '../../../context/menuContext';
import { Loading } from '../Loading';
import { Error } from '../Error';
import { formatScale } from '../../../utils/ChartUtils/chart-util';
import {
  formatCurrencyDisplay,
} from '../../../utils/FormatUtils/formatCurrencyValue';
import { today } from '../../../utils/date-utils';
import { isAfter, isBefore, isEqual } from 'date-fns';

export function Summary({ timeFilter }) {
  const classes = useStyles();
  const [filter, setFilter] = useState('Total Sales');
  const { selectedRestaurant } = useMenuContext();
  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorRequestId, setErrorRequestId] = useState(null);
  const [typeFilter, setTypeFilter] = useState({
    delivery: true,
    takeout: true,
  });
  const [todayData, setTodayData] = useState(null);

  const infoType = useMemo(
    () =>
      ({
        'Total Sales': 'sales',
        'Total Food Cost': 'wholesale',
        'Total Base Price': 'inhouse',
        'Total Gross Margin': 'profit',
      }[filter]),
    [filter]
  );

  const showToday = useMemo(() => {
    if (!timeFilter?.start || !timeFilter?.end) return false;
    const { start: todayStart, end: todayEnd } = today();
    const rangeStart = timeFilter.start;
    const rangeEnd = timeFilter.end;
    const startInclusive = (d1, d2) => isBefore(d1, d2) || isEqual(d1, d2);
    return startInclusive(rangeStart, todayEnd) && startInclusive(todayStart, rangeEnd);
  }, [timeFilter]);

  useEffect(() => {
    if (selectedRestaurant?.id)
      (async () => {
        try {
          setError(false);
          setErrorRequestId(null);
          setLoading(true);
          const response = await getSales(
            selectedRestaurant.id,
            { ...timeFilter, details: 1 },
            'sales-summary'
          );
          setData(response);
        } catch (err) {
          setErrorRequestId(
            err?.requestId || err?.__apiContext?.requestId || null
          );
          setError(true);
        } finally {
          setLoading(false);
        }
      })();
  }, [selectedRestaurant, timeFilter]);

  useEffect(() => {
    if (showToday && selectedRestaurant?.id)
      (async () => {
        try {
          const response = await getSales(
            selectedRestaurant.id,
            today(),
            'sales-today'
          );
          setTodayData(response);
        } catch (_err) {
          setTodayData(null);
        }
      })();
    else {
      setTodayData(null);
    }
  }, [showToday, selectedRestaurant]);

  const handleCheckbox = useCallback(
    (type) => {
      setTypeFilter((old) => ({ ...old, [type]: !old[type] }));
    },
    [setTypeFilter]
  );

  const activeFilter = useMemo(() => {
    if (typeFilter.delivery === typeFilter.takeout) return 'total';
    if (!typeFilter.delivery && typeFilter.takeout) return 'takeout';
    return 'delivery';
  }, [typeFilter]);

  return (
    <Box
      className={classes.infoContainer}
      style={{ flex: 2 }}
      data-testid="summary"
    >
      <Box className={classes.filterWrapper}>
        <Filter
          filter={filter}
          setFilter={setFilter}
          className={classes.filter}
        />
        <Box display="flex" alignItems="center">
          <Checkbox
            inputProps={{
              'data-testid': 'delivery-checkbox',
            }}
            checked={typeFilter.delivery}
            onChange={() => handleCheckbox('delivery')}
            color="primary"
            size="small"
          />
          <Typography variant="caption">Delivery</Typography>
          <Checkbox
            inputProps={{
              'data-testid': 'takeout-checkbox',
            }}
            checked={typeFilter.takeout}
            onChange={() => handleCheckbox('takeout')}
            color="primary"
            size="small"
          />
          <Typography variant="caption">Takeout</Typography>
        </Box>
      </Box>
      {loading ? (
        <Loading />
      ) : (
        <>
          {error ? (
            <Box
              flex={1}
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <Error requestId={errorRequestId} />
            </Box>
          ) : (
            <Box
              width="100%"
              style={{ overflowX: 'auto', overflowY: 'hidden' }}
            >
              <Box height="300px" minWidth="600px">
                <BarChart
                  data={data[infoType][activeFilter]}
                  background
                  colors={['#0058FF']}
                  keys={['value']}
                  gridY={false}
                  index="day"
                  axisBottom={{
                    tickSize: 0,
                    tickPadding: 8,
                    tickRotation: 0,
                  }}
                  axisLeft={{
                    tickSize: 0,
                    tickPadding: 8,
                    tickRotation: 0,
                    format: formatScale,
                  }}
                  margin={{ top: 30, right: 0, bottom: 32, left: 50 }}
                  borderRadius={{
                    top: {
                      left: 4,
                      right: 4,
                    },
                    bottom: {
                      left: 4,
                      right: 4,
                    },
                  }}
                />
              </Box>
            </Box>
          )}
        </>
      )}
      {showToday && todayData && (
        <>
          <Divider style={{ margin: '16px 0 12px' }} />
          <Typography
            variant="subtitle2"
            style={{ fontWeight: 600, marginBottom: '8px', fontSize: '13px' }}
          >
            Today's Earnings
          </Typography>
          <Typography variant="h6" style={{ marginBottom: '12px' }}>
            {formatCurrencyDisplay(todayData.total)}
          </Typography>
          <Box display="flex" gap="24px">
            <Box display="flex" alignItems="center" gap="6px">
              <DirectionsCar style={{ fontSize: '16px', opacity: 0.6 }} />
              <Typography variant="caption">Delivery</Typography>
              <Typography variant="caption" style={{ fontWeight: 600 }}>
                {formatCurrencyDisplay(todayData.delivery)}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap="6px">
              <DirectionsWalk style={{ fontSize: '16px', opacity: 0.6 }} />
              <Typography variant="caption">Takeout</Typography>
              <Typography variant="caption" style={{ fontWeight: 600 }}>
                {formatCurrencyDisplay(todayData.takeout)}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

Summary.propTypes = {
  timeFilter: PropTypes.shape({
    start: PropTypes.object,
    end: PropTypes.object,
    interval: PropTypes.string,
  }),
};

export default Summary;
